import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Play, Plus, Check, ArrowLeft, Clock, Calendar, User, Tv, ChevronDown, Lock, ShoppingCart, Heart, Share2, Star, Flag } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { MovieRow } from '../components/MovieRow';
import { StarRating, RatingDisplay } from '../components/StarRating';
import { DonationPopup } from '../components/DonationPopup';
import PaymentMethodModal from '../components/PaymentMethodModal';
import { getPendingPurchase, clearPendingPurchase } from '../components/PaymentMethodModal';
import AdBannerPair from '../components/AdBannerPair';
import AdSenseBanner, { AD_SLOTS } from '../components/AdSenseBanner';
import PopunderScript from '../components/PopunderScript';
import { filmsApi, myListApi, ratingsApi, seriesApi, premiumContentApi, reportsApi, formatDuration, thumbUrl } from '../lib/api';
import { toTitleCase } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import CreatorProfileModal from '../components/CreatorProfileModal';
import EpisodeDetailPopup from '../components/EpisodeDetailPopup';

const FilmDetailPage = () => {
  const { filmId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [film, setFilm] = useState(null);
  const [relatedFilms, setRelatedFilms] = useState([]);
  const [inMyList, setInMyList] = useState(false);
  const [myRating, setMyRating] = useState(0);
  const [loading, setLoading] = useState(true);
  const [episodes, setEpisodes] = useState([]);
  const [seasons, setSeasons] = useState({});
  const [selectedSeason, setSelectedSeason] = useState(1);
  const [premiumAccess, setPremiumAccess] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [episodeRatings, setEpisodeRatings] = useState({});
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showDescPopup, setShowDescPopup] = useState(false);
  const [selectedEpisode, setSelectedEpisode] = useState(null);

  // Hero preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const bgRef = useRef(null);
  const previewRef = React.useRef(null);
  const previewTimerRef = React.useRef(null);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [filmId]);

  // Smooth parallax via refs + rAF — background only (no foreground transform to avoid layout shifts)
  useEffect(() => {
    const isSmall = window.innerWidth < 1024;
    const bgSpeed = isSmall ? 0.12 : 0.3;
    let rafId = null;
    let ticking = false;
    function update() {
      const y = window.scrollY;
      if (bgRef.current) bgRef.current.style.transform = 'translate3d(0,' + y * bgSpeed + 'px,0)';
      ticking = false;
    }
    function onScroll() {
      if (!ticking) { rafId = requestAnimationFrame(update); ticking = true; }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => { window.removeEventListener('scroll', onScroll); if (rafId) cancelAnimationFrame(rafId); };
  }, []);

  // Auto-open purchase modal if redirected back from add-card flow
  useEffect(() => {
    const pending = getPendingPurchase();
    if (pending?.autoOpen && pending.paymentType === 'premium_content' && pending.filmId === filmId) {
      clearPendingPurchase();
      setTimeout(() => setShowPurchaseModal(true), 500);
    }
  }, [filmId]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const filmData = await filmsApi.getById(filmId);
        setFilm(filmData);

        // Fetch episodes if series
        if (filmData.content_type === 'series') {
          try {
            const epData = await seriesApi.getEpisodes(filmId);
            setEpisodes(epData.episodes || []);
            setSeasons(epData.seasons || {});
            const seasonNums = Object.keys(epData.seasons || {}).map(Number).sort((a, b) => a - b);
            if (seasonNums.length > 0) setSelectedSeason(seasonNums[0]);
            // Fetch episode ratings
            try {
              const epRatings = await ratingsApi.getEpisodeRatings(filmId);
              setEpisodeRatings(epRatings.ratings || {});
            } catch (e) { console.error('Failed to fetch episode ratings:', e); }
          } catch (e) { console.error('Failed to fetch episodes:', e); }
        }

        // Fetch related films (same genre)
        if (filmData.genres?.length > 0) {
          const related = await filmsApi.getAll({ genre: filmData.genres[0], limit: 10 });
          setRelatedFilms(related.filter((f) => f.film_id !== filmId));
        }

        // Check my list status
        if (isAuthenticated) {
          try {
            const listStatus = await myListApi.check(filmId);
            setInMyList(listStatus.in_list);

            const ratingData = await ratingsApi.getMyRating(filmId);
            setMyRating(ratingData.rating || 0);

            // Check premium content access
            if (filmData.is_premium_content) {
              const accessData = await premiumContentApi.checkAccess(filmId);
              setPremiumAccess(accessData);
            }
          } catch (error) {
            console.error('Failed to fetch user data:', error);
          }
        }
      } catch (error) {
        console.error('Failed to fetch film:', error);
        toast.error('Film not found');
        navigate('/browse');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filmId, isAuthenticated, navigate]);

  // Hero video preview: 2s image → fade-in → 20s video from 30s mark → fade-out → image
  useEffect(() => {
    if (!film?.video_file || film.content_type === 'series') return;
    const timer = setTimeout(() => {
      if (previewRef.current) {
        previewRef.current.load();
        setShowPreview(true);
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [film]);

  useEffect(() => {
    if (!showPreview || !previewRef.current) return;
    const video = previewRef.current;
    let cancelled = false;
    let metadataTimeout = null;

    const startPreview = () => {
      if (cancelled) return;
      if (metadataTimeout) clearTimeout(metadataTimeout);
      const dur = video.duration || 0;
      // Skip preview if video is very short
      if (dur < 5) {
        setShowPreview(false);
        return;
      }
      // Start from 30s mark for longer videos, or from 0 for shorter ones
      const startTime = dur >= 30 ? 30 : 0;
      const playDuration = dur >= 30 ? Math.min(20, dur - 30) : Math.min(20, dur);
      video.currentTime = startTime;

      const onSeeked = () => {
        video.removeEventListener('seeked', onSeeked);
        if (cancelled) return;
        video.play().then(() => {
          if (cancelled) return;
          setPreviewVisible(true);
          previewTimerRef.current = setTimeout(() => {
            if (cancelled) return;
            setPreviewVisible(false);
            setTimeout(() => {
              if (previewRef.current) previewRef.current.pause();
              setShowPreview(false);
            }, 1000);
          }, playDuration * 1000);
        }).catch(() => {
          setShowPreview(false);
        });
      };

      video.addEventListener('seeked', onSeeked, { once: true });
    };

    const onError = () => {
      if (!cancelled) setShowPreview(false);
    };
    video.addEventListener('error', onError, { once: true });

    if (video.readyState >= 1) {
      startPreview();
    } else {
      video.addEventListener('loadedmetadata', startPreview, { once: true });
      // Timeout: give up after 15s if metadata hasn't loaded
      metadataTimeout = setTimeout(() => {
        if (!cancelled) {
          video.removeEventListener('loadedmetadata', startPreview);
          setShowPreview(false);
        }
      }, 15000);
    }

    return () => {
      cancelled = true;
      clearTimeout(previewTimerRef.current);
      if (metadataTimeout) clearTimeout(metadataTimeout);
      video.removeEventListener('loadedmetadata', startPreview);
      video.removeEventListener('error', onError);
    };
  }, [showPreview]);

  const handleAddToList = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      if (inMyList) {
        await myListApi.remove(filmId);
        setInMyList(false);
        toast.success('Removed from My List');
      } else {
        await myListApi.add(filmId);
        setInMyList(true);
        toast.success('Added to My List');
      }
    } catch (error) {
      toast.error('Failed to update list');
    }
  };

  const handleRate = async (rating) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      await ratingsApi.rate(filmId, rating);
      setMyRating(rating);
      toast.success('Rating saved');
      
      // Refresh film data to get updated average
      const updated = await filmsApi.getById(filmId);
      setFilm(updated);
    } catch (error) {
      toast.error('Failed to save rating');
    }
  };

  const handleRateEpisode = async (episodeId, rating) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    try {
      const result = await ratingsApi.rateEpisode(episodeId, rating);
      setEpisodeRatings(prev => ({
        ...prev,
        [episodeId]: {
          ...prev[episodeId],
          my_rating: rating,
          avg_rating: result.avg_rating,
          rating_count: result.rating_count,
        }
      }));
      toast.success('Episode rating saved');
    } catch (error) {
      toast.error('Failed to save episode rating');
    }
  };

  const handlePurchase = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    setShowPurchaseModal(true);
  };

  const handlePurchaseSuccess = () => {
    setPremiumAccess({ has_access: true });
    setPurchasing(false);
  };

  const handlePlayClick = () => {
    // If premium content and not purchased, show purchase option
    if (film?.is_premium_content && (!premiumAccess || !premiumAccess.has_access)) {
      handlePurchase();
    } else if (film?.content_type === 'series' && episodes.length > 0) {
      // For series, navigate with the first episode
      navigate(`/watch/${film.film_id}?episode=${episodes[0].episode_id}`);
    } else {
      navigate(`/watch/${film.film_id}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030014] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!film) return null;

  return (
    <div className="min-h-screen bg-[#030014]" data-testid="film-detail-page">
      <PopunderScript />
      <Navbar />

      {/* Hero Section */}
      <div className="relative w-full">
        {/* Background layer — contained with overflow-hidden so image/video never leak */}
        <div className="absolute inset-0 overflow-hidden">
          <div
            ref={bgRef}
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${thumbUrl(film.thumbnail, 1280)})`, willChange: 'transform' }}
          />
          {/* Video preview */}
          {film.video_file && film.content_type !== 'series' && (
            <div
              className="absolute inset-0 z-[1]"
              style={{
                opacity: previewVisible ? 1 : 0,
                transition: 'opacity 1s ease-in-out',
                willChange: 'opacity',
                pointerEvents: 'none',
              }}
              data-testid="preview-wrapper"
            >
              <video
                ref={previewRef}
                src={`${process.env.REACT_APP_BACKEND_URL}${film.video_file}`}
                className="w-full h-full object-cover"
                preload="auto"
                muted
                playsInline
                webkit-playsinline=""
                onEnded={() => {
                  setPreviewVisible(false);
                  setTimeout(() => setShowPreview(false), 1000);
                }}
                onError={() => {
                  setShowPreview(false);
                  setPreviewVisible(false);
                }}
                data-testid="hero-preview-video"
              />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#030014] via-[#030014]/60 to-transparent z-[2]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#030014]/50 via-transparent to-transparent z-[2]" />
        </div>

        {/* Back Button */}
        <button
          onClick={() => navigate('/browse')}
          className="absolute top-20 md:top-24 left-4 md:left-8 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-black/50 backdrop-blur-sm text-white/80 hover:text-white hover:bg-black/70 transition-all"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>

        {/* Content — always relative so hero grows naturally with content */}
        <div className="relative z-[3] flex flex-col justify-end pt-24 md:pt-28 pb-8 md:pb-12 lg:pb-16 min-h-[75vh] md:min-h-[80vh] w-full px-4 md:px-8 lg:px-[4%]">
          <div className="max-w-3xl space-y-2 sm:space-y-3 md:space-y-5">
            {/* Genres */}
            <div className="flex flex-wrap gap-2">
              {film.genres.map((genre) => (
                <span key={genre} className="badge">
                  {genre}
                </span>
              ))}
            </div>

            {/* Title */}
            <div className="flex items-center gap-3">
              <h1
                className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold tracking-tighter"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                data-testid="film-title"
              >
                {toTitleCase(film.title)}
              </h1>
              {film.content_type === 'series' && (
                <span className="px-3 py-1 text-sm font-bold rounded-lg bg-[#00FF9D]/20 text-[#00FF9D] border border-[#00FF9D]/30 flex items-center gap-1 self-start mt-2">
                  <Tv className="w-4 h-4" /> Series
                </span>
              )}
            </div>

            {/* Metadata Row */}
            <div className="flex flex-wrap items-center gap-4 md:gap-6 text-sm">
              {film.content_rating && (
                <span className="px-2 py-0.5 text-xs font-bold rounded bg-white/10 border border-white/20 text-white tracking-wide" data-testid="content-rating-badge">
                  {film.content_rating}
                </span>
              )}
              <RatingDisplay rating={film.avg_rating} count={film.rating_count} />
              
              <div className="flex items-center gap-1 text-gray-300">
                <Calendar className="w-4 h-4" />
                {film.year > 0
                  ? film.year
                  : film.release_date
                    ? new Date(film.release_date).getFullYear()
                    : film.created_at
                      ? new Date(film.created_at).getFullYear()
                      : new Date().getFullYear()}
              </div>
              
              {/* Duration — only for non-series */}
              {film.content_type !== 'series' && film.duration > 0 && (
                <div className="flex items-center gap-1 text-gray-300">
                  <Clock className="w-4 h-4" />
                  {formatDuration(film.duration)}
                </div>
              )}
            </div>

            {/* Creator with Support Button */}
            <div className="flex items-center flex-wrap gap-3">
              <div className="flex items-center gap-2 text-gray-400">
                <User className="w-4 h-4" />
                <span>Created by <button onClick={() => setSelectedCreator(film.creator)}
                  className="text-[#A78BFA] hover:text-[#8B5CF6] hover:underline transition-colors cursor-pointer"
                  data-testid="creator-name-link">{film.creator}</button></span>
              </div>
              {film.company_id && film.creator_has_merch && (
                <button
                  onClick={() => navigate(`/shop/creator/${film.company_id}`)}
                  className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#00FF9D]/10 text-[#00FF9D] text-sm font-medium hover:bg-[#00FF9D]/20 transition-colors border border-[#00FF9D]/30"
                  data-testid="visit-shop-btn"
                >
                  <ShoppingCart className="w-4 h-4" />
                  Visit Shop
                </button>
              )}
            </div>

            {/* Premium Content Badge */}
            {film.is_premium_content && (
              <div className="flex items-center gap-2 bg-yellow-500/20 text-yellow-400 px-4 py-2 rounded-lg w-fit">
                <Lock className="w-4 h-4" />
                <span className="font-medium">Premium Content • ${film.premium_price?.toFixed(2)}</span>
                {premiumAccess?.has_access && (
                  <span className="ml-2 text-[#00FF9D] text-sm">(Purchased)</span>
                )}
              </div>
            )}

            {/* Description — truncated on mobile/tablet, full on large screens */}
            <p
              className="text-sm md:text-base lg:text-lg text-gray-300 leading-relaxed line-clamp-3 md:line-clamp-4 lg:line-clamp-none cursor-pointer lg:cursor-default"
              onClick={() => { if (window.innerWidth < 1024) setShowDescPopup(true); }}
              data-testid="film-description"
            >
              {film.description}
            </p>
            {film.description && film.description.length > 120 && (
              <button
                className="text-[#A78BFA] text-sm font-medium lg:hidden"
                onClick={() => setShowDescPopup(true)}
                data-testid="read-more-btn"
              >
                Read more
              </button>
            )}

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-3 md:gap-4 pt-2 md:pt-4">
              {/* Play/Purchase Button */}
              {film.is_scheduled ? (
                <div className="flex items-center gap-3">
                  <div className="btn-primary flex items-center gap-2 bg-[#8B5CF6]/20 text-[#8B5CF6] cursor-default border border-[#8B5CF6]/30" data-testid="coming-soon-badge">
                    <Calendar className="w-5 h-5" />
                    Coming Soon
                  </div>
                  {film.release_date && (
                    <span className="text-sm text-gray-400">
                      {new Date(film.release_date + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </span>
                  )}
                </div>
              ) : film.is_premium_content && (!premiumAccess || !premiumAccess.has_access) ? (
                film.creator_stripe_connected ? (
                <button
                  onClick={handlePurchase}
                  disabled={purchasing}
                  className="btn-primary flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-black"
                  data-testid="purchase-button"
                >
                  {purchasing ? (
                    <>
                      <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="w-5 h-5" />
                      Purchase for ${film.premium_price?.toFixed(2)}
                    </>
                  )}
                </button>
                ) : (
                <div className="btn-primary flex items-center gap-2 bg-gray-600/20 text-gray-400 cursor-default border border-gray-600/30" data-testid="purchase-unavailable">
                  <ShoppingCart className="w-5 h-5" />
                  Purchase currently unavailable
                </div>
                )
              ) : (
                <button
                  onClick={handlePlayClick}
                  className="btn-white flex items-center gap-2"
                  data-testid="play-button"
                >
                  <Play className="w-5 h-5 fill-current" />
                  Play Now
                </button>
              )}

              {/* Trailer Button */}
              {(film.trailer_youtube_id || film.trailer_vimeo_id) && (
                <button
                  onClick={() => navigate(`/watch/${film.film_id}?trailer=true`)}
                  className="flex items-center gap-2 px-6 py-3 rounded-lg border border-white/30 text-white/70 hover:text-white hover:border-white/50 transition-all"
                  data-testid="watch-trailer-button"
                >
                  <Play className="w-4 h-4" />
                  Watch Trailer
                </button>
              )}

              <button
                onClick={handleAddToList}
                className="btn-secondary flex items-center gap-2"
                data-testid="mylist-button"
              >
                {inMyList ? (
                  <>
                    <Check className="w-5 h-5" />
                    In My List
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5" />
                    Add to List
                  </>
                )}
              </button>

              {/* Share Button */}
              <button
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: film.title,
                      text: film.description?.substring(0, 100) || `Check out ${film.title} on reframes`,
                      url: window.location.href,
                    }).catch(() => {});
                  } else {
                    navigator.clipboard.writeText(window.location.href);
                    toast.success('Link copied to clipboard!');
                  }
                }}
                className="flex items-center gap-2 px-5 py-3 rounded-lg border border-white/20 text-white/70 hover:text-white hover:border-white/40 transition-all"
                data-testid="share-button"
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>

              {/* Donate Button - only if creator has Stripe connected */}
              {film.company_id && film.creator_stripe_connected && (
                <DonationPopup
                  creatorId={film.company_id}
                  creatorName={film.creator}
                  filmId={film.film_id}
                  trigger={
                    <button
                      className="flex items-center gap-2 px-5 py-3 rounded-lg border border-pink-500/30 text-pink-400 hover:bg-pink-500/10 transition-all"
                      data-testid="donate-button"
                    >
                      <Heart className="w-4 h-4" />
                      Donate
                    </button>
                  }
                />
              )}
            </div>

            {/* Report Content Button - discreet */}
            {isAuthenticated && (
              <div className="mt-3">
                <button
                  onClick={() => setShowReportModal(true)}
                  className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-red-400 transition-colors"
                  data-testid="report-content-btn"
                >
                  <Flag className="w-3 h-3" />
                  Report content
                </button>
              </div>
            )}

            {/* User Rating */}
            {isAuthenticated && (
              <div className="pt-4 border-t border-white/10">
                <p className="text-sm text-gray-400 mb-2">Your Rating</p>
                <StarRating rating={myRating} onRate={handleRate} size="lg" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ADBANNER1 (Adsterra): Between Hero and content */}
      <div className="w-full px-4 md:px-8 lg:px-[4%] py-6">
        <AdBannerPair />
      </div>

      {/* Episodes Section (for Series) */}
      {film.content_type === 'series' && episodes.length > 0 && (
        <div className="w-full px-4 md:px-8 lg:px-[4%] py-8" data-testid="episodes-section">
          <div className="flex items-center gap-4 mb-6">
            <Tv className="w-6 h-6 text-[#00FF9D]" />
            <h2 className="text-xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Episodes
            </h2>
            {Object.keys(seasons).length > 1 && (
              <div className="relative">
                <select
                  value={selectedSeason}
                  onChange={(e) => setSelectedSeason(Number(e.target.value))}
                  className="bg-[#0F0B1E] border border-[#8B5CF6]/30 rounded-lg px-4 py-2 text-sm appearance-none pr-8 cursor-pointer"
                  data-testid="season-selector"
                >
                  {Object.keys(seasons).sort((a, b) => a - b).map((s) => (
                    <option key={s} value={s}>Season {s}</option>
                  ))}
                </select>
                <ChevronDown className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" />
              </div>
            )}
          </div>
          <div className="space-y-3">
            {(seasons[selectedSeason] || episodes.filter(ep => ep.season_number === selectedSeason)).map((ep) => {
              const epRating = episodeRatings[ep.episode_id] || {};
              return (
              <div
                key={ep.episode_id}
                className="admin-card p-4 flex items-center gap-4 hover:border-[#8B5CF6]/40 transition-colors group"
                data-testid={`episode-${ep.episode_id}`}
              >
                {/* Mobile: compact layout (title + play) */}
                <div className="flex md:hidden items-center gap-3 w-full">
                  <div className="w-9 h-9 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-[#8B5CF6]">{ep.episode_number}</span>
                  </div>
                  <span className="flex-1 font-medium text-sm truncate">{ep.title}</span>
                  <button
                    onClick={() => setSelectedEpisode(ep)}
                    className="w-9 h-9 rounded-full bg-[#00FF9D]/20 flex items-center justify-center flex-shrink-0 active:scale-95 transition-transform"
                    data-testid={`ep-play-mobile-${ep.episode_id}`}
                  >
                    <Play className="w-4 h-4 text-[#00FF9D] fill-[#00FF9D]" />
                  </button>
                </div>

                {/* Desktop: full layout (unchanged) */}
                <div className="hidden md:flex items-center gap-4 w-full">
                  <div className="w-12 h-12 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center flex-shrink-0 cursor-pointer"
                    onClick={() => navigate(`/watch/${film.film_id}?episode=${ep.episode_id}`)}>
                    <span className="text-lg font-bold text-[#8B5CF6]">{ep.episode_number}</span>
                  </div>
                  {ep.thumbnail && (
                    <img src={ep.thumbnail} alt={ep.title} className="w-32 h-20 object-cover rounded cursor-pointer"
                      onClick={() => navigate(`/watch/${film.film_id}?episode=${ep.episode_id}`)} />
                  )}
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/watch/${film.film_id}?episode=${ep.episode_id}`)}>
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium">S{ep.season_number}E{ep.episode_number}: {ep.title}</h3>
                    </div>
                    {ep.description && <p className="text-sm text-gray-400 line-clamp-1">{ep.description}</p>}
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0" onClick={e => e.stopPropagation()}>
                    {epRating.avg_rating > 0 && (
                      <span className="text-xs text-gray-500" data-testid={`ep-avg-rating-${ep.episode_id}`}>
                        {epRating.avg_rating?.toFixed(1)} ({epRating.rating_count})
                      </span>
                    )}
                    <div className="flex gap-0.5" data-testid={`ep-rating-stars-${ep.episode_id}`}>
                      {[1, 2, 3, 4, 5].map(v => (
                        <button key={v}
                          onClick={() => handleRateEpisode(ep.episode_id, v)}
                          onMouseEnter={e => e.currentTarget.parentNode.dataset.hover = v}
                          onMouseLeave={e => delete e.currentTarget.parentNode.dataset.hover}
                          className="p-0.5 transition-colors"
                          data-testid={`ep-star-${ep.episode_id}-${v}`}
                        >
                          <Star className={`w-4 h-4 ${v <= (epRating.my_rating || 0) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600 hover:text-yellow-400'}`} />
                        </button>
                      ))}
                    </div>
                  </div>
                  {ep.duration > 0 && (
                    <span className="text-sm text-gray-500 flex-shrink-0">{formatDuration(ep.duration)}</span>
                  )}
                  <Play className="w-5 h-5 text-gray-400 flex-shrink-0 group-hover:text-[#8B5CF6] transition-colors cursor-pointer"
                    onClick={() => navigate(`/watch/${film.film_id}?episode=${ep.episode_id}`)} />
                </div>
              </div>
              );
            })}
          </div>

          {/* Mobile Episode Detail Popup */}
          <EpisodeDetailPopup
            open={!!selectedEpisode}
            onOpenChange={(open) => { if (!open) setSelectedEpisode(null); }}
            episode={selectedEpisode}
            episodeRating={selectedEpisode ? episodeRatings[selectedEpisode.episode_id] : null}
            allEpisodes={seasons[selectedSeason] || episodes.filter(ep => ep.season_number === selectedSeason)}
            onRate={handleRateEpisode}
            onWatch={(ep) => navigate(`/watch/${film.film_id}?episode=${ep.episode_id}`)}
            onSelectEpisode={setSelectedEpisode}
          />
        </div>
      )}

      {/* Related Films */}
      {relatedFilms.length > 0 && (
        <div className="w-full px-4 md:px-8 lg:px-[4%] py-12">
          <MovieRow title="You Might Also Like" films={relatedFilms} />
        </div>
      )}

      {/* ADBANNER4: Before footer */}
      <div className="w-full px-4 md:px-8 lg:px-[4%] pb-8">
        <AdSenseBanner slot={AD_SLOTS.ADBANNER4} testId="ad-banner-4" maxWidth={970} />
      </div>

      {selectedCreator && (
        <CreatorProfileModal
          creatorName={selectedCreator}
          onClose={() => setSelectedCreator(null)}
        />
      )}

      {/* Report Content Modal */}
      {showReportModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setShowReportModal(false)} data-testid="report-modal">
          <div className="bg-[#0F0B1E] border border-white/10 rounded-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <Flag className="w-5 h-5 text-red-400" />
              <h3 className="text-lg font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Report Content</h3>
            </div>
            <p className="text-sm text-gray-400 mb-4">Please describe why you're reporting "{film.title}"</p>
            <textarea
              value={reportReason}
              onChange={e => setReportReason(e.target.value)}
              placeholder="Enter the reason for reporting (required)"
              className="w-full h-28 bg-white/5 border border-white/10 rounded-lg p-3 text-sm text-white placeholder-gray-500 resize-none focus:outline-none focus:border-[#8B5CF6]/50"
              data-testid="report-reason-input"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => { setShowReportModal(false); setReportReason(''); }}
                className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
                data-testid="report-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!reportReason.trim()) { toast.error('Please enter a reason'); return; }
                  setReportSubmitting(true);
                  try {
                    await reportsApi.report(filmId, reportReason.trim());
                    toast.success('Report submitted. Thank you.');
                    setShowReportModal(false);
                    setReportReason('');
                  } catch (err) {
                    toast.error(err.message || 'Failed to submit report');
                  } finally {
                    setReportSubmitting(false);
                  }
                }}
                disabled={reportSubmitting || !reportReason.trim()}
                className="px-4 py-2 text-sm font-medium bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="report-submit-btn"
              >
                {reportSubmitting ? 'Submitting...' : 'Submit Report'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Premium Content Purchase Modal */}
      <PaymentMethodModal
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
        paymentType="premium_content"
        amount={film?.premium_price || 0}
        filmId={filmId}
        creatorName={film?.creator}
        onSuccess={handlePurchaseSuccess}
      />

      {/* Description Popup (mobile/tablet) */}
      {showDescPopup && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-end lg:items-center justify-center p-0 lg:p-4"
          onClick={() => setShowDescPopup(false)}
          data-testid="desc-popup-overlay"
        >
          <div
            className="bg-[#0F0B1E] w-full lg:max-w-lg lg:rounded-2xl rounded-t-2xl max-h-[80vh] overflow-y-auto border-t lg:border border-white/10"
            onClick={e => e.stopPropagation()}
            data-testid="desc-popup"
          >
            <div className="sticky top-0 bg-[#0F0B1E] p-4 pb-2 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {toTitleCase(film.title)}
              </h3>
              <button
                onClick={() => setShowDescPopup(false)}
                className="p-1 rounded-full hover:bg-white/10 text-gray-400"
                data-testid="desc-popup-close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-4 pb-24">
              <p className="text-gray-300 leading-relaxed text-sm">{film.description}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilmDetailPage;
